import { Flex, CircularProgress } from '@chakra-ui/react';
import { createContext, useContext, useState, useEffect, FC, PropsWithChildren } from 'react';
import { TranslationDict, TranslationSettings } from '~/monolith-api';
import { useEditTranslationSettings, useTranslations } from '~/queries';
import { Locale } from 'date-fns';

type UILocalizationOptions = {
   translations: TranslationDict;
   translationSettings: TranslationSettings;
   handleTranslationSettingsChange: (value: TranslationSettings) => any;
   dateFnsLocale?: Locale;
};

class UILocalization {
   public translations: TranslationDict = {};

   public translationSettings: TranslationSettings;

   public handleTranslationSettingsChange: (value: TranslationSettings) => any;

   dateFnsLocale?: Locale;

   isMissingDateFnsLocale: boolean;

   constructor(
      {
         translations,
         translationSettings,
         handleTranslationSettingsChange,
         dateFnsLocale,
      }: UILocalizationOptions = UILocalization.defaultOptions
   ) {
      this.translations = translations;
      this.translationSettings = translationSettings;
      this.handleTranslationSettingsChange = handleTranslationSettingsChange;
      this.dateFnsLocale = dateFnsLocale;

      // en does not have a dateFnsLocale because it is the default
      this.isMissingDateFnsLocale = translationSettings.locale !== 'en' && !dateFnsLocale;
   }

   static defaultOptions: UILocalizationOptions = {
      translations: {},
      translationSettings: {
         locale: 'en',
      },
      handleTranslationSettingsChange: () => void 0,
   };

   /**
    * Translates an english string into the user's current language.
    *
    * Example: var buttonText = _js('Done');
    *          var buttonText = _js('Delete my %1', 'guide');
    *
    * This performs the translation on the client-side, which means that we
    * won't have to localize the js file into every language when it is built.
    */
   _js = (origString: string, ...replacements: (string | number)[]) => {
      //cast numbers to strings
      const processedReplacements = replacements.map(r => (typeof r === 'number' ? `${r}` : r));
      const compactedString = String(origString).replace(/\s{2,}/g, ' ');

      var translated = this.translations[compactedString] || compactedString;

      return processedReplacements.reduce(function (translated, replacement, i) {
         var search = new RegExp('%' + (i + 1), 'g');
         return translated.replace(search, replacement);
      }, translated);
   };

   /**
    * Uses one of two string formats to translate a string depending on whether
    * or not the `number` provided is singular.
    *
    * Example: var stepText = __p(stepCount, '%1 Step', '%1 Steps', stepCount);
    */
   ___p = (number: number, singleString: string, pluralString: string, ...args: string[]) => {
      return number === 1 ? this._js(singleString, ...args) : this._js(pluralString, ...args);
   };
}

const LocalizationContext = createContext<UILocalization>(new UILocalization());

export const LocalizationProvider: FC<PropsWithChildren> = ({ children }) => {
   const { isLoading, data } = useTranslations();

   const editTranslationSettings = useEditTranslationSettings();

   const [dateFnsLocale, setDateFnsLocale] = useState<undefined | Locale>();

   let options: undefined | UILocalizationOptions;

   if (data) {
      const { translations, translationSettings } = data;
      options = {
         translations,
         translationSettings,
         handleTranslationSettingsChange(translationSettings: TranslationSettings) {
            editTranslationSettings.mutate(translationSettings, {
               onSuccess: () => window.location.reload(),
            });
         },
         dateFnsLocale,
      };
   }

   const localization = new UILocalization(options);

   useEffect(() => {
      import(`date-fns/locale/${localization.translationSettings.locale}/index.js`)
         .then(module => {
            setDateFnsLocale(module?.default);
         })
         .catch(() => {
            setDateFnsLocale(undefined);
         });
   }, [localization.translationSettings.locale]);

   if (isLoading) {
      return (
         <Flex align="center" justify="center" flex={1} height="100vh">
            <CircularProgress isIndeterminate />
         </Flex>
      );
   }

   return (
      <LocalizationContext.Provider value={localization}>{children}</LocalizationContext.Provider>
   );
};

export function useLocalization() {
   return useContext(LocalizationContext);
}
